// ABOUTME: Shared relative/dynamic date selector for the reporthub (chart pill + settings stacked card).
// ABOUTME: Renders the "past X days" + timeframe toggle and a dynamic ShDateRangePicker; styling is injected per call site via sx.
import { Box, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../theme";
import { calculateDaysBetween, chartDateOptions, chartDateTimeFrames, chartRollingUnits, normalizeFrame, rollingUnitToFrame } from "../chartFilterEnums";
import NumberInput from "../../../theme/inputs/NumberInput";
import ShDateRangePicker from "../../../theme/ShDateRangePicker";
import { RhRollingUnitDropDown, RhTimeframe, RhTimeframeDropDown } from "./RhTimeframe";

// Structural styles shared by every pill instance. Colours are intentionally NOT set here —
// each call site injects its own via sx/groupSx/buttonSx so its look stays byte-for-byte identical.
const structuralButton = {
    flex: 1,
    padding: 0,
    paddingX: 2,
    border: 0,
    lineHeight: "32px",
    textTransform: "unset",
    borderRadius: "32px !important",
    transition: "250ms",
};

// Hide the browser's native number spinners inside the day input — the pill relies on
// typing (hideButtons) and the stacked card already has its own +/- buttons.
// Exported so ReportDetailPageBuilder's inline copies of this pill can apply the same fix.
export const nativeSpinnerSuppression = {
    "& input[type=number]": {
        "-moz-appearance": "textfield",
        "&::-webkit-outer-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
        },
        "&::-webkit-inner-spin-button": {
            "-webkit-appearance": "none",
            margin: 0,
        },
    },
};

// Extra layout the DYNAMIC button needs when selected so the inline date picker sits inline.
const dynamicSelectedLayout = {
    "&.Mui-selected": {
        pr: 0.5,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        "@media screen and (max-width: 47.9375em)": {
            justifyContent: "center",
        },
    },
};

export const RhDateSelector = ({
    currentReport,
    setCurrentReport,
    variant = "pill",
    showTimeframe = true,
    separator = "..",
    sx,
    groupSx,
    buttonSx,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const date = currentReport?.date;

    const handleTypeChange = (e, newType) => {
        if (!newType) return;

        setCurrentReport(prev => {
            if (newType === chartDateOptions.RELATIVE) {
                return {
                    ...prev,
                    date: {
                        ...prev.date,
                        type: chartDateOptions.RELATIVE,
                        days: 7,
                        frame: chartDateTimeFrames.ONEDAY,
                    },
                };
            }

            if (newType === chartDateOptions.DYNAMIC) {
                const end = new Date();
                const start = new Date();
                start.setDate(end.getDate() - 30);

                const format = d => d.toISOString().slice(0, 10); // YYYY-MM-DD

                return {
                    ...prev,
                    date: {
                        ...prev.date,
                        type: chartDateOptions.DYNAMIC,
                        from: format(start),
                        to: format(end),
                    },
                };
            }

            if (newType === chartDateOptions.ROLLING) {
                return {
                    ...prev,
                    date: {
                        ...prev.date,
                        type: chartDateOptions.ROLLING,
                        unit: chartRollingUnits.WEEK,
                        frame: rollingUnitToFrame[chartRollingUnits.WEEK],
                        compare: [{ type: "offset", n: 1 }],
                    },
                };
            }

            return prev;
        });
    };

    const handleDaysChange = (e) => {
        setCurrentReport(prev => {
            const days = e;
            const frame = normalizeFrame(prev.date.frame, days);

            return {
                ...prev,
                date: {
                    ...prev.date,
                    days,
                    frame,
                },
            };
        });
    };

    const handleRangeSelect = (value) => {
        setCurrentReport(prev => {
            const from = value[0].formattedStartDate;
            const to = value[0].formattedEndDate;

            const days = calculateDaysBetween(from, to);
            const frame = normalizeFrame(prev.date.frame, days);

            return {
                ...prev,
                date: {
                    ...prev.date,
                    from,
                    to,
                    frame,
                },
            };
        });
    };

    // Stacked card layout — single consumer (MRhSettings), so its exact styling is baked in.
    if (variant === "stacked") {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    borderRadius: 5,
                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                    ...sx,
                }}
            >
                <ToggleButtonGroup
                    value={date?.type}
                    exclusive
                    onChange={handleTypeChange}
                    aria-label="Active"
                    sx={{
                        width: 1,
                        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                        padding: 0.5,
                        borderRadius: 5,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        transition: "250ms",
                    }}
                >
                    <ToggleButton
                        sx={{
                            ...structuralButton,
                            color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            "&.Mui-selected": {
                                background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.grey[400],
                                fontWeight: "bold",
                                borderBottomLeftRadius: "0px !important",
                            },
                            "&.Mui-selected:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                            },
                        }}
                        value={chartDateOptions.RELATIVE}
                    >
                        <span style={{ flexShrink: 0 }}>{t('relative')}</span>
                        <span>&nbsp;{separator}&nbsp;</span>
                        <span style={{ flexShrink: 0 }}>{t("in_days")}</span>
                    </ToggleButton>
                    <ToggleButton
                        sx={{
                            ...structuralButton,
                            color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            "&.Mui-selected": {
                                background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.grey[400],
                                fontWeight: "bold",
                                borderBottomLeftRadius: "0px !important",
                                borderBottomRightRadius: "0px !important",
                            },
                            "&.Mui-selected:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                            },
                        }}
                        value={chartDateOptions.ROLLING}
                    >
                        {t('current')}
                    </ToggleButton>
                    <ToggleButton
                        sx={{
                            ...structuralButton,
                            color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            "&.Mui-selected": {
                                background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.grey[400],
                                fontWeight: "bold",
                                borderBottomRightRadius: "0px !important",
                            },
                            "&.Mui-selected:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                            },
                        }}
                        value={chartDateOptions.DYNAMIC}
                    >
                        {t('date_range')}
                    </ToggleButton>
                </ToggleButtonGroup>

                {date?.type === chartDateOptions.RELATIVE &&
                    <Box sx={{ p: 2, width: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                        <NumberInput btnSize={"24"}
                            height={32}
                            min={1}
                            max={366}
                            value={date?.days}
                            change={handleDaysChange}
                            inputSX={{
                                background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[100],
                                ...nativeSpinnerSuppression,
                            }}
                        />
                    </Box>
                }
                {date?.type === chartDateOptions.DYNAMIC &&
                    <Box sx={{ width: 1, p: 2 }}>
                        <ShDateRangePicker
                            sx={{
                                background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[100],
                            }}
                            startDate={date?.from}
                            endDate={date?.to}
                            size={32}
                            select={handleRangeSelect}
                            explicit
                        />
                    </Box>
                }
                {date?.type === chartDateOptions.ROLLING &&
                    <Box sx={{ p: 2, width: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                        <RhRollingUnitDropDown currentReport={currentReport} setCurrentReport={setCurrentReport} />
                    </Box>
                }

                {showTimeframe &&
                    <Box sx={{ p: 2, pt: 0 }}>
                        <RhTimeframe currentReport={currentReport} setCurrentReport={setCurrentReport} />
                    </Box>
                }
            </Box>
        );
    }

    // Pill layout — horizontal toggle with inline controls (chart / pivot). Default colours match the chart.
    const baseButtonSx = {
        ...structuralButton,
        color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
        "&.Mui-selected": {
            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
            color: colors.txt["primary"],
            fontWeight: "bold",
        },
        ...buttonSx,
    };

    return (
        <Box
            sx={{
                height: 40,
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                p: 0.5,
                borderRadius: 5,
                display: "flex",
                gap: 1.5,
                ...sx,
            }}
        >
            <ToggleButtonGroup
                value={date?.type}
                exclusive
                onChange={handleTypeChange}
                aria-label="Active"
                sx={{
                    width: 1,
                    background: colors.grey[200],
                    borderRadius: 5,
                    transition: "250ms",
                    ...groupSx,
                }}
            >
                <ToggleButton sx={baseButtonSx} value={chartDateOptions.RELATIVE}>
                    <span style={{ flexShrink: 0 }}>{t('relative')}</span>
                    {date?.type === chartDateOptions.RELATIVE ?
                        <Box sx={{ mx: 1, width: 48,display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                            <NumberInput btnSize={"16"}
                                hideButtons
                                height={24}
                                min={1}
                                max={366}
                                value={date?.days}
                                change={handleDaysChange}
                                inputSX={nativeSpinnerSuppression}
                            />
                        </Box>
                        :
                        <span>&nbsp;{separator}&nbsp;</span>
                    }
                    <span style={{ flexShrink: 0 }}>{t("in_days")}</span>
                </ToggleButton>
                <ToggleButton sx={[baseButtonSx, dynamicSelectedLayout]} value={chartDateOptions.ROLLING}>
                    {t('current')}
                    {date?.type === chartDateOptions.ROLLING &&
                        <RhRollingUnitDropDown currentReport={currentReport} setCurrentReport={setCurrentReport} />
                    }
                </ToggleButton>
                <ToggleButton sx={[baseButtonSx, dynamicSelectedLayout]} value={chartDateOptions.DYNAMIC}>
                    {t('date_range')}
                    {date?.type === chartDateOptions.DYNAMIC &&
                        <ShDateRangePicker
                            startDate={date?.from}
                            endDate={date?.to}
                            size={24}
                            select={handleRangeSelect}
                            explicit
                        />
                    }
                </ToggleButton>
            </ToggleButtonGroup>
            {showTimeframe &&
                <RhTimeframeDropDown currentReport={currentReport} setCurrentReport={setCurrentReport} />
            }
        </Box>
    );
};
